import React, { useState } from "react";

import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { ApplicationEndpoints } from "@generated/application";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { Application, DeployMode } from "src/domains/application/types";

// ── Deploy Mode Selector ─────────────────────────────────────────────────────

export const DEPLOY_MODE_OPTIONS: {
  value: DeployMode;
  label: string;
  description: string;
  infraNote?: string;
}[] = [
  {
    value: DeployMode.RUSHA_MANAGED,
    label: "Rusha Managed",
    description: "Rusha builds and deploys your stack automatically.",
    infraNote: "K8s deployments use GitOps (ArgoCD/Flux).",
  },
  {
    value: DeployMode.CI_BUILD_RUSHA_DEPLOY,
    label: "CI Build → Rusha Deploy",
    description:
      "Your CI pipeline builds images. After CI completes, it calls the deploy-callback endpoint to trigger Rusha deploy.",
    infraNote: "K8s deployments use GitOps (ArgoCD/Flux).",
  },
  {
    value: DeployMode.RUSHA_BUILD_CI_DEPLOY,
    label: "Rusha Build → CI Deploy",
    description:
      "Rusha builds images and pushes to your registry. Your CI pipeline handles deployment.",
    infraNote: "Requires CI pipeline configured for deployment.",
  },
  {
    value: DeployMode.CI_MANAGED,
    label: "CI Managed",
    description:
      "Your CI pipeline controls the full build and deploy cycle. Rusha only provisions infrastructure.",
    infraNote: "Cloud-native serverless deployments use CI/CD in VCS.",
  },
];

interface DeployModeSelectorProps {
  system: Application;
}

export function DeployModeSelector({
  system,
}: DeployModeSelectorProps): React.ReactElement {
  const [mode, setMode] = useState<string>(
    system.deployMode ?? DeployMode.RUSHA_MANAGED,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = async (newMode: string) => {
    if (newMode === mode) return;
    setSaving(true);
    setError(null);
    try {
      const baseUrl = apiBaseUrls.BACKEND;
      await axios.patch(
        `${baseUrl}${ApplicationEndpoints.UPDATE_APPLICATION.replace(":id", system.alias)}`,
        { deployMode: newMode },
        {
          headers: {
            Authorization: `Bearer ${orgSessionManager.getToken() ?? ""}`,
          },
        },
      );
      setMode(newMode);
    } catch {
      setError("Failed to update deploy mode.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 1,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <RocketLaunchIcon fontSize="small" color="action" />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Deploy Mode</InputLabel>
          <Select
            value={mode}
            label="Deploy Mode"
            onChange={(e) => void handleChange(e.target.value)}
            disabled={saving}
          >
            {DEPLOY_MODE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {saving && <CircularProgress size={16} />}
        {error && (
          <Typography variant="caption" color="error.main">
            {error}
          </Typography>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {DEPLOY_MODE_OPTIONS.find((o) => o.value === mode)?.description}
      </Typography>
      {DEPLOY_MODE_OPTIONS.find((o) => o.value === mode)?.infraNote && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
        >
          {DEPLOY_MODE_OPTIONS.find((o) => o.value === mode)?.infraNote}
        </Typography>
      )}
    </Box>
  );
}

export default DeployModeSelector;
